<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import LensMark from '@/components/lens/LensMark.vue';
import { renderMarkdown } from '@/common/markdown';
import { openHelp } from '@/common/help';
import '@/styles/help-content.css';

const props = defineProps({
    // [{ role: 'user' | 'assistant', content, error?: { message, code }, result?: { kind, … } }]
    messages: { type: Array, default: () => [] },
    isStreaming: Boolean,
    firstName: { type: String, default: '' },
    // The page the panel was opened on: its name for the greeting and up to three starter questions.
    page: { type: Object, default: () => ({ label: '', starters: [] }) },
    // The help guides for this page, offered on an empty transcript: [{ id, title, url }]
    pageGuides: { type: Array, default: () => [] },
});

const emit = defineEmits(['send', 'stop', 'newChat']);

const router = useRouter();

// auto-grow caps at max-rows × 24px lines + 16px padding; this stops the box at 150px, then it scrolls.
const MAX_ROWS = (150 - 16) / 24;
const NEW_CHAT_COMMANDS = ['/new', '/clear'];

const draft = ref('');
const scroller = ref(null);
const input = ref(null);

const greeting = computed(() => {
    const hi = props.firstName ? `Hi ${props.firstName}` : 'Hi';
    return props.page?.label ? `${hi}, you're on ${props.page.label}. How can I help?` : `${hi}, how can I help?`;
});

// A reply's guide links: a 'guides' result, or a result from before results had a kind.
const citationsOf = (result) => (result?.kind === 'guides' || (result && !result.kind) ? (result.citations ?? []) : []);

// A Help link opens the Help window; anything else is a page in the app.
function openLink(href) {
    if (href.startsWith('/help/')) openHelp(href);
    else router?.push(href);
}

function sendStarter(starter) {
    if (props.isStreaming) return;
    emit('send', starter);
}
const canSend = computed(() => !props.isStreaming && draft.value.trim().length > 0);

// "Thinking…" until the reply being streamed has its first text.
const isThinking = computed(() => {
    if (!props.isStreaming) return false;
    const last = props.messages.at(-1);
    return !last || last.role !== 'assistant' || !last.content?.trim();
});

// The reply still streaming: it keeps a spinner at its foot until the whole reply has arrived.
const isStreamingReply = (index) => props.isStreaming && index === props.messages.length - 1;

// A reply's HTML can be empty of text while it streams (a lone "---" renders as a bare rule).
const hasVisibleText = (html) => !!html.replace(/<[^>]*>/g, '').trim();

// While a reply streams, a spinning Lens mark trails its last word, like a typing cursor. The mark is
// copied from a hidden LensMark below, scoped attributes and all, so LensMark's own styles spin it.
const cursorSource = ref(null);
function withCursor(html) {
    const mark = cursorSource.value?.innerHTML;
    if (!mark) return html;
    const template = document.createElement('template');
    template.innerHTML = html;
    const blocks = template.content.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote');
    const cursor = `<span class="bn-lens-chat__cursor" aria-hidden="true">${mark}</span>`;
    const last = blocks[blocks.length - 1];
    if (last) last.insertAdjacentHTML('beforeend', cursor);
    else template.innerHTML += cursor;
    return template.innerHTML;
}

function send() {
    const text = draft.value.trim();
    if (NEW_CHAT_COMMANDS.includes(text.toLowerCase())) {
        draft.value = '';
        emit('newChat');
        return;
    }
    if (!canSend.value) return;
    emit('send', text);
    draft.value = '';
}

function focus() {
    nextTick(() => input.value?.focus());
}

defineExpose({ focus });

// Only follow the stream when the reader is already at the bottom, so scrolling up to reread
// isn't yanked back on every token.
// Follows new messages and the reply as it streams, but never past the top of the latest reply:
// once a long reply fills the panel, its beginning stays in view. A reader who scrolled up to
// reread is left alone.
function scrollToBottom(force = false) {
    const el = scroller.value;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (!force && !nearBottom) return;
    nextTick(() => {
        const bottom = el.scrollHeight - el.clientHeight;
        const replies = el.querySelectorAll('[data-cy="lensPanelAssistantMessage"]');
        // The latest reply's bubble exists once its first text arrives; until then follow to the bottom.
        const last = props.messages.at(-1);
        const reply = last?.role === 'assistant' && last.content ? replies[replies.length - 1] : null;
        const replyTop = reply ? reply.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 16 : bottom;
        el.scrollTop = Math.min(bottom, replyTop);
    });
}

watch(
    () => props.messages.length,
    () => scrollToBottom(true),
);

watch(
    () => [props.messages.at(-1)?.content, props.isStreaming],
    () => scrollToBottom(),
);
</script>

<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0">
        <div
            ref="scroller"
            class="flex-grow-1 overflow-y-auto pa-4 d-flex flex-column ga-2"
            style="min-height: 0"
            role="log"
            aria-live="polite"
            aria-label="Lens conversation"
            data-cy="lensPanelMessages"
        >
            <div v-if="!messages.length" class="ma-auto d-flex flex-column align-center ga-3 text-center text-medium-emphasis" data-cy="lensPanelGreeting">
                <div class="text-body-1">{{ greeting }}</div>
                <div v-if="page?.starters?.length" class="d-flex flex-column align-center ga-2" data-cy="lensPanelStarters">
                    <v-chip v-for="starter in page.starters" :key="starter" label color="primary" class="bn-lens-chat__starter py-1" data-cy="lensPanelStarter" @click="sendStarter(starter)">
                        {{ starter }}
                    </v-chip>
                </div>
                <div v-if="pageGuides.length" class="d-flex flex-column align-center ga-2" data-cy="lensPanelPageGuides">
                    <div class="text-body-2">Guides for this page</div>
                    <v-chip v-for="guide in pageGuides" :key="guide.id" label color="primary" class="bn-lens-chat__starter py-1" data-cy="lensPanelPageGuide" @click="openHelp(guide.url)">
                        {{ guide.title }}
                    </v-chip>
                </div>
            </div>
            <template v-for="(message, index) in messages" :key="index">
                <div v-if="message.role === 'user'" class="d-flex justify-end">
                    <div class="bn-lens-chat__bubble bn-lens-chat__bubble--user bg-primary text-pre-wrap" data-cy="lensPanelUserMessage">{{ message.content }}</div>
                </div>
                <template v-else>
                    <!-- Whitespace alone isn't a reply yet: "Thinking…" stays until there's something to read. -->
                    <div v-if="message.content?.trim()" class="d-flex">
                        <div class="bn-lens-chat__bubble bn-lens-chat__bubble--assistant bn-help-content bn-help-content--compact bg-surface" data-cy="lensPanelAssistantMessage">
                            <!-- renderMarkdown sanitizes with DOMPurify; links the model writes are shown as plain text. -->
                            <template v-for="html in [renderMarkdown(message.content, { links: false })]" :key="html">
                                <!-- eslint-disable-next-line vue/no-v-html -->
                                <div v-if="hasVisibleText(html)" v-html="isStreamingReply(index) ? withCursor(html) : html" />
                                <div v-else-if="isStreamingReply(index)" class="d-flex align-center ga-2 text-medium-emphasis" data-cy="lensPanelStillAnswering">
                                    <LensMark :size="16" spinning />
                                    <span>Loading response</span>
                                </div>
                                <span v-if="isStreamingReply(index) && hasVisibleText(html)" class="d-sr-only" data-cy="lensPanelStillAnswering">Lens is still answering</span>
                            </template>
                        </div>
                    </div>
                    <!-- The reply's result, by kind; any other kind shows only the reply's text. -->
                    <div v-if="citationsOf(message.result).length" class="bn-help-content bn-help-content--compact d-flex flex-column ga-2" data-cy="lensPanelCitations">
                        <a
                            v-for="citation in citationsOf(message.result)"
                            :key="citation.id"
                            :href="citation.url"
                            class="bn-help-guide"
                            data-cy="lensPanelCitation"
                            @click.prevent="openHelp(citation.url)"
                        >
                            <span>
                                <span class="bn-help-guide__kicker">Read the full guide</span>
                                {{ citation.title }}
                            </span>
                            <font-awesome-icon :icon="['far', 'arrow-up-right-from-square']" class="bn-help-guide__arrow" />
                        </a>
                    </div>
                    <div v-else-if="message.result?.kind === 'pageLink' && message.result.href" class="bn-help-content bn-help-content--compact d-flex flex-column" data-cy="lensPanelPageLink">
                        <a :href="message.result.href" class="bn-help-guide" @click.prevent="openLink(message.result.href)">
                            <span>{{ message.result.label }}</span>
                            <font-awesome-icon :icon="['far', message.result.href.startsWith('/help/') ? 'arrow-up-right-from-square' : 'arrow-right']" class="bn-help-guide__arrow" />
                        </a>
                    </div>
                    <v-alert v-if="message.error" type="error" variant="outlined" density="compact" class="text-body-2" data-cy="lensPanelError">
                        {{ message.error.message }}
                    </v-alert>
                </template>
            </template>
            <!-- The source of the trailing spinner copied into streaming replies (withCursor). -->
            <span ref="cursorSource" hidden><LensMark :size="16" spinning /></span>
            <div v-if="isThinking" class="d-flex" data-cy="lensPanelThinking">
                <div class="bn-lens-chat__bubble bn-lens-chat__bubble--assistant bg-surface d-flex align-center ga-2 text-medium-emphasis">
                    <LensMark :size="16" spinning />
                    <span>Thinking…</span>
                </div>
            </div>
        </div>

        <div class="flex-grow-0 px-3 pb-3">
            <!-- Send sits inside the box at the bottom right, clear of the scrollbar on the far right edge. -->
            <div class="position-relative">
                <v-textarea
                    ref="input"
                    v-model="draft"
                    class="bn-lens-chat__input"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    rows="1"
                    auto-grow
                    :max-rows="MAX_ROWS"
                    hide-details
                    placeholder="Ask Lens"
                    aria-label="Message Lens"
                    data-cy="lensPanelPrompt"
                    @keydown.enter.exact.prevent="send"
                />
                <v-btn v-if="isStreaming" icon class="bn-lens-chat__send" variant="text" size="small" color="error" aria-label="Stop" title="Stop" data-cy="lensPanelStop" @click="emit('stop')">
                    <font-awesome-icon :icon="['far', 'stop-circle']" size="lg" />
                </v-btn>
                <v-btn
                    v-else
                    icon
                    class="bn-lens-chat__send"
                    variant="text"
                    size="small"
                    color="primary"
                    :disabled="!canSend"
                    aria-label="Send"
                    title="Send"
                    data-cy="lensPanelSend"
                    @mousedown.prevent
                    @click="send"
                >
                    <font-awesome-icon :icon="['far', 'paper-plane']" size="lg" />
                </v-btn>
            </div>
            <div class="mt-1 text-right text-caption text-disabled">Enter to send · Shift+Enter for a new line</div>
        </div>
    </div>
</template>

<style scoped>
/* The spinner trailing a streaming reply's last word. */
.bn-lens-chat__bubble :deep(.bn-lens-chat__cursor) {
    display: inline-flex;
    margin-left: 4px;
    vertical-align: -2px;
}

/* Starters are whole questions: let them wrap instead of truncating. */
.bn-lens-chat__starter {
    height: auto;
    white-space: normal;
    text-align: center;
}

/* The textarea runs to the box's right edge so its scrollbar sits there; its right padding keeps
   text clear of the send button laid over the bottom right. */
.bn-lens-chat__input :deep(.v-field) {
    padding-inline-end: 0;
}

/* Focus shows as a thin primary outline, not Vuetify's heavy 2px dark one. */
.bn-lens-chat__input :deep(.v-field--focused .v-field__outline) {
    --v-field-border-width: 1px;
    --v-field-border-opacity: 0.6;
}

.bn-lens-chat__input :deep(textarea) {
    padding-inline-end: 56px;
}

.bn-lens-chat__send {
    position: absolute;
    right: 18px;
    bottom: 2px;
}

.bn-lens-chat__bubble {
    max-width: 85%;
    min-width: 0;
    padding: 8px 12px;
    border-radius: 16px;
    overflow-wrap: anywhere;
}

.bn-lens-chat__bubble--user {
    border-bottom-right-radius: 4px;
}

.bn-lens-chat__bubble--assistant {
    border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-bottom-left-radius: 4px;
}
</style>
